import { getNutritionEntityDisplayName } from "../shared/nutritionEntityNameUtils";

function hasText(value) {
  return typeof value === "string" && value.trim().length > 0;
}

function getFiniteNumber(value, fallback = 0) {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
}

function getRecipeSearchText(recipe, displayName, t) {
  const typeKey = String(recipe?.typeKey ?? "main-string").trim() || "main-string";

  return [
    displayName,
    recipe?.name,
    recipe?.description,
    typeof t === "function" ? t(typeKey) : typeKey,
  ]
    .filter(hasText)
    .join(" ")
    .toLowerCase();
}

export function normalizeRecipeBrowserItem(recipe, { language = "en", t } = {}) {
  if (!recipe || typeof recipe !== "object") {
    return null;
  }

  const displayName = getNutritionEntityDisplayName("recipe", recipe, language);

  return {
    id: String(recipe?.id ?? displayName).trim(),
    typeKey: String(recipe?.typeKey ?? "main-string").trim() || "main-string",
    name: displayName,
    order: getFiniteNumber(recipe?.order),
    nutrients: {
      calories: getFiniteNumber(recipe?.calories),
      carbs: getFiniteNumber(recipe?.carbs),
      protein: getFiniteNumber(recipe?.protein),
      fats: getFiniteNumber(recipe?.fats),
    },
    searchText: getRecipeSearchText(recipe, displayName, t),
    sortName: displayName.toLowerCase(),
    sourceRecord: recipe,
  };
}

export function buildRecipeBrowserItems(recipes, options = {}) {
  return (Array.isArray(recipes) ? recipes : [])
    .map((recipe) => normalizeRecipeBrowserItem(recipe, options))
    .filter(Boolean);
}

export function matchesRecipeBrowserFilter(item, filterKey) {
  if (filterKey === "all-string") {
    return true;
  }

  if (filterKey === "main-string") {
    return (
      item?.typeKey === "main-string" ||
      item?.typeKey === "lunch-string" ||
      item?.typeKey === "dinner-string"
    );
  }

  if (filterKey === "breakfast-string" || filterKey === "snack-string") {
    return item?.typeKey === filterKey;
  }

  return false;
}

export function matchesRecipeBrowserSearch(item, searchText) {
  const normalizedSearch = String(searchText ?? "")
    .trim()
    .toLowerCase();

  if (!normalizedSearch) {
    return true;
  }

  return String(item?.searchText ?? "").includes(normalizedSearch);
}

export function sortRecipeBrowserItems(items, sortField = null) {
  if (!sortField) {
    return [...items].sort(
      (leftItem, rightItem) => leftItem.order - rightItem.order,
    );
  }

  return [...items].sort((leftItem, rightItem) => {
    const leftValue = getFiniteNumber(leftItem?.nutrients?.[sortField]);
    const rightValue = getFiniteNumber(rightItem?.nutrients?.[sortField]);

    if (rightValue !== leftValue) {
      return rightValue - leftValue;
    }

    return String(leftItem?.sortName ?? "").localeCompare(
      String(rightItem?.sortName ?? ""),
      undefined,
      { sensitivity: "base" },
    );
  });
}

export function getRecipeBrowserItemKey(item) {
  return item?.id || null;
}
